import type { NurseWithRelation } from "@/types/nurse-type";
import { NurseTableRow } from "./NurseTableRow";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useMemo } from "react";
import { headerList } from "./rosterTable.config";
import { cn } from "@/utils/cn";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";

export function ManagerTable({
  nurseDataWithRelation,
  selectedGroupId,
  activeNurseIdForSideProfile,
  selectIdForSideprofile,
  isPending,
  sortConfig,
  onSort,
  disableRowEdit = false,
  isAddNewNurse = false,
  setIsAddNewNurse,
  hideEditColumn = false,
  customHeaderList,
  addNurseModal = false,
  teamName,
  enableSelection = false,
  selectedNurses,
  onToggleSelection,
  className,
}: {
  teamName: string;
  nurseDataWithRelation: NurseWithRelation[];
  selectedGroupId: string | null;
  activeNurseIdForSideProfile: string | null;
  selectIdForSideprofile?: (bool: boolean, nurseID: string) => void;
  isPending?: boolean;
  sortConfig?: {
    key: "experience" | "grade" | "name" | null;
    order: "asc" | "desc" | null;
  };
  onSort?: (key: "experience" | "grade" | "name") => void;
  disableRowEdit?: boolean;
  isAddNewNurse?: boolean;
  setIsAddNewNurse?: React.Dispatch<React.SetStateAction<boolean>>;
  hideEditColumn?: boolean;
  customHeaderList?: typeof headerList;
  addNurseModal?: boolean;
  enableSelection?: boolean;
  selectedNurses?: Set<string>;
  onToggleSelection?: (nurseId: string) => void;
  className?: string;
}) {
  const filteredHeaderList = useMemo(() => {
    let headers = customHeaderList || headerList;

    if (!customHeaderList && hideEditColumn) {
      headers = headers.filter((h) => h.label !== "수정");
    }

    if (!enableSelection) {
      headers = headers.filter(
        (h) => h.label.trim().toLowerCase() !== "checkbox"
      );
    }

    return headers;
  }, [hideEditColumn, customHeaderList, enableSelection]);

  const newNurseTemplate: NurseWithRelation = useMemo(
    () => ({
      nurse_id: `temp_new_${Date.now()}`,
      account_id: "",
      name: "",
      emp_num: 0,
      gender: "M",
      age: null,
      role: "RN",
      grade: 0,
      experience: 1,
      is_head_nurse: false,
      weekly_off_weekday: null,
      is_night_nurse: [],
      preceptor_id: null,
      joining_date: null,
      resignation_date: null,
      nurse_memo: "",
      active: 1,
      team_id: null,
      sequence: 0,
      is_weekend_off: false,
      fixed_shift: null,
      preceptor: null,
      preceptees: [],
      group_id: selectedGroupId || "",
      level_: "일반",
      office_id: "",
      personal_off_adjustment: 0,
      enable_aide: false,
      enable_nurse_pair_preference: false,
      wanted_max_requests: null,
      resignation_reason: null,
      resignation_reason_memo: null,
    }),
    [selectedGroupId]
  );

  const displayNurses = useMemo(() => {
    if (isAddNewNurse && !disableRowEdit) {
      return [newNurseTemplate, ...nurseDataWithRelation];
    }
    return nurseDataWithRelation;
  }, [isAddNewNurse, disableRowEdit, newNurseTemplate, nurseDataWithRelation]);

  return (
    <div
      className={cn(
        "border shadow-lg rounded-b-md overflow-visible block bg-white",
        className
      )}
      aria-label={teamName}
    >
      <Table className="w-auto" style={{ tableLayout: "fixed" }}>
        <colgroup>
          {filteredHeaderList.slice(1).map((header, idx) => {
            return <col key={idx} style={{ width: header.width }} />;
          })}
        </colgroup>
        <TableHeader
          className={cn(
            "bg-[#F0F0F2]",
            (enableSelection || addNurseModal) && "sticky top-0 z-8"
          )}
        >
          <TableRow>
            {filteredHeaderList.slice(1).map((header, idx) => {
              if (header.label.trim().toLowerCase() === "checkbox") {
                return <TableHead key={idx} className="text-center" />;
              }

              const isSortable =
                header.label === "경력" ||
                header.label === "Grade" ||
                header.label === "이름";
              const sortKey =
                header.label === "경력"
                  ? "experience"
                  : header.label === "Grade"
                    ? "grade"
                    : header.label === "이름"
                      ? "name"
                      : null;
              const isActive = sortKey !== null && sortConfig?.key === sortKey;
              const isWeeklyOff = header.label === "주휴";

              return (
                <TableHead
                  key={idx}
                  className={`text-center ${
                    isSortable
                      ? "cursor-pointer select-none hover:bg-gray-200"
                      : ""
                  }`}
                  onClick={() => isSortable && sortKey && onSort?.(sortKey)}
                >
                  <div className="flex items-center justify-center gap-1">
                    <span
                      className={cn(
                        isSortable && "underline",
                        isActive && "!text-sig !underline font-bold",
                        addNurseModal &&
                          "!text-text-main2 !text-[14px] !font-bold !no-underline font-pretendard"
                      )}
                    >
                      {header.label}
                    </span>
                    {isActive && (
                      <span className="text-sig text-sm">
                        {sortConfig?.order === "asc" ? "↑" : "↓"}
                      </span>
                    )}

                    {isWeeklyOff && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              설정된 순환주기에 따라 주휴 변동일이 적용됩니다.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        {isPending ? (
          <TableBody>
            <TableRow>
              <TableCell
                colSpan={filteredHeaderList.length - 1}
                className="text-center py-10"
              >
                <div className="flex justify-center items-center gap-2">
                  <span className="text-zinc-500">
                    팀 정보를 동기화 중입니다...
                  </span>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <TableBody>
            {displayNurses.map((nurse, idx) => (
              <NurseTableRow
                key={nurse.nurse_id}
                nurse={nurse}
                idx={idx}
                selectedGroupId={selectedGroupId}
                activeForSideProfile={
                  nurse.nurse_id === activeNurseIdForSideProfile
                }
                selectIdForSideprofile={selectIdForSideprofile}
                enableDragHandle={false}
                disableEdit={disableRowEdit}
                onCancelNewNurse={() => setIsAddNewNurse?.(false)}
                hideEditColumn={hideEditColumn}
                visibleColumns={
                  customHeaderList
                    ? customHeaderList
                        .slice(1)
                        .map((h) => h.label)
                        .filter((label) => label !== "")
                    : undefined
                }
                enableSelection={enableSelection}
                isSelected={selectedNurses?.has(nurse.nurse_id)}
                onToggleSelection={onToggleSelection}
              />
            ))}
            {displayNurses.length === 0 && (
              <TableRow className="bg-white!">
                <TableCell
                  colSpan={filteredHeaderList.length - 1}
                  className="text-center py-5"
                >
                  {addNurseModal ? (
                    <div className="flex items-center justify-center h-[200px]">
                      <span className="font-pretendard text-[14px] text-text-main2">
                        추가할 근무자를 선택하고
                        <span className="font-pretendard text-sig font-bold">
                          {" "}
                          [추가하기]
                        </span>
                        버튼을 눌러 근무자를 추가해주세요.
                      </span>
                    </div>
                  ) : (
                    "등록된 인원이 없습니다."
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        )}
      </Table>
    </div>
  );
}
